import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AdminIdentity } from '@logica/shared';
import { authApi, getCsrfToken } from '../api/client';

type AuthState = {
  admin: AdminIdentity | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<AdminIdentity | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([authApi.me(), getCsrfToken()])
      .then(([result]) => setAdmin(result.admin))
      .finally(() => setLoading(false));
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      admin,
      loading,
      login: async (email, password) => setAdmin(await authApi.login(email, password)),
      logout: async () => {
        await authApi.logout();
        setAdmin(null);
      }
    }),
    [admin, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider.');
  return value;
}
